import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FaExclamationCircle } from 'react-icons/fa';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import {
  Button,
  Grid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useBanner,
} from '@tq/ui-components';
import { FormProvider } from '@tq/ui-components/dist/components/forms-engine';
import { Consultee } from 'types';
import { useDeleteConsultee } from 'api';
import { BANNER_TIMEOUT } from 'config';

type UserDeletionModalProps = {
  isOpen: boolean;
  userToDelete: Consultee | undefined;
  onClose: () => void;
};

export const UserDeletionModal: React.FC<UserDeletionModalProps> = ({
  isOpen,
  userToDelete,
  onClose,
}) => {
  const { t } = useTranslation('userdeletion_modal');
  const { mutate: deleteConsultee } = useDeleteConsultee();
  const banner = useBanner();

  const handleCancel = () => {
    onClose();
  };

  const handleError = () => {
    onClose();
    banner({
      id: 'delete-user-banner-fail',
      title: t(
        'banner_user_removed_failed',
        '{{consulteeName}} could not be deleted. Please try again.',
        { consulteeName: userToDelete?.consulteeName }
      ),
      duration: BANNER_TIMEOUT,
      status: 'error',
    });
  };

  const handleConfirm = () => {
    if (userToDelete)
      deleteConsultee(userToDelete?.consulteeId, {
        onSuccess: () => {
          onClose();
          banner({
            id: 'remove-user-banner-success',
            title: t(
              'banner_user_removed_success',
              '{{consulteeName}} has been successfully deleted',
              {
                consulteeName: userToDelete?.consulteeName,
              }
            ),
            duration: BANNER_TIMEOUT,
            status: 'success',
          });
        },
        onError: () => {
          handleError();
        },
      });
  };

  return (
    <Modal isOpen={isOpen} onClose={() => handleCancel()}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader aria-label={t('delete_user_modal', 'Delete user')}>
          <Grid gridAutoFlow='column' gridGap={4}>
            <FaExclamationCircle color={COLOURS.red[400]} />
            <span>{t('delete_user_header', 'Delete user')}</span>
          </Grid>
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody overflow='visible'>
          <FormProvider>
            {userToDelete && (
              <p id='deletion-message'>
                <Trans
                  i18nKey='user_deletion_message'
                  defaults='You are about to permanently delete the user <bold>{{consulteeName}}</bold> <bold>{{email}}</bold>. Once deleted the user will no longer be able to access the Consultee Hub and all information about the organisation, team(s) and permissions will be lost. If you wish to continue select delete.'
                  values={{
                    consulteeName: `${userToDelete?.consulteeName}`,
                    email: `${userToDelete?.email}`,
                  }}
                  components={{ bold: <strong /> }}
                />
              </p>
            )}
          </FormProvider>
        </ModalBody>
        <ModalFooter justifyContent='end'>
          <Grid gridAutoFlow='column' gridGap={4}></Grid>
          <Button variant='tertiary' onClick={() => handleCancel()}>
            {t('cancel_button_text', 'Cancel')}
          </Button>
          <Button variant='danger' onClick={() => handleConfirm()}>
            {t('confirm_button_text', 'Confirm')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
